import React, { useMemo } from 'react';
import { Head, usePage } from '@inertiajs/react';

import { rawInertiaTitle } from '@/lib/inertia-title';

interface MetaTag {
  tagName: string;
  headKey?: string;
  innerContent?: string | Record<string, unknown>;
  httpEquiv?: string;
  [key: string]: unknown;
}

function stringifyInnerContent(innerContent: MetaTag['innerContent']) {
  if (innerContent == null) return undefined;
  return typeof innerContent === 'string' ? innerContent : JSON.stringify(innerContent);
}

function escapeJsonForScriptTag(content: string) {
  return content
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

export default function InertiaMetaTags() {
  // biome-ignore lint/style/useNamingConvention: prop name is set by inertia_rails gem
  const { _inertia_meta: meta } = usePage().props as { _inertia_meta?: MetaTag[] };

  const [titleTag, otherTags] = useMemo(() => {
    const tags = meta ?? [];

    let title: MetaTag | undefined;
    const rest: MetaTag[] = [];
    for (const tag of tags) {
      if (!title && tag.tagName === 'title') {
        title = tag;
      } else {
        rest.push(tag);
      }
    }
    return [title, rest] as const;
  }, [meta]);

  const title = stringifyInnerContent(titleTag?.innerContent);

  return (
    <Head title={title ? rawInertiaTitle(title) : undefined}>
      {otherTags.map(({ tagName, innerContent, headKey, httpEquiv, ...attrs }) => {
        const stringifiedInnerContent = stringifyInnerContent(innerContent);
        const isJsonLdScript = tagName === 'script' && attrs.type === 'application/ld+json';
        const contentProps = isJsonLdScript
          ? {
              dangerouslySetInnerHTML: {
                // biome-ignore lint/style/useNamingConvention: __html is required by React's dangerouslySetInnerHTML API
                __html: escapeJsonForScriptTag(stringifiedInnerContent ?? ''),
              },
            }
          : {};

        return React.createElement(
          tagName,
          {
            key: headKey ?? `${tagName}-${JSON.stringify(attrs)}`,
            'head-key': headKey,
            ...(httpEquiv ? { httpEquiv } : {}),
            ...attrs,
            ...contentProps,
          },
          isJsonLdScript ? undefined : stringifiedInnerContent,
        );
      })}
    </Head>
  );
}
